import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIsMutating } from '@tanstack/react-query';
import { useCheckIsSuperAdmin, useGlobalModal } from 'hooks';
import { APL_KEY, useGetAPLGrid } from 'hooks/apl/useGetAPLGrid';

import { Form } from 'antd';

import { CustomNotification } from 'common/components';

import {
  createMasterApl,
  updateApl,
  rollbackMasterAPL,
  getAPLDetail,
} from 'services/apl';

import {
  toggleReloadGridApl,
  resetReloadGridApl,
} from 'pages/branded-products-apl/controllers/actions';

import selectorUser from '@redux/user/selectors';

import { getAplDetail } from 'pages/product-apl-detail/controllers/actions';

import { SPREADSHEET_FILE_TYPE } from 'static/Constants';
import {
  COMPANY_ADMINISTRATOR,
  COMPANY_PRODUCT_MANAGER_ROLE,
  MASTER_APL_NAME,
} from 'pages/home/ribbon/components/sections/product-apl/constants';

const ROLLBACK_MASTER_APL_KEY = ['apl', 'roll-back-master-apl'];

export const CREATE_APL_KEY = ['apl', 'create-apl'];
export const UPDATE_APL_KEY = ['apl', 'update-apl'];

const getAPLDetailKey = (id) => ['apl', 'get-apl-detail', Number(id)];

export const useCreateAPL = () => {
  const createMasterAPLMutation = useMutation({
    mutationKey: CREATE_APL_KEY,
    mutationFn: createMasterApl,
  });

  return createMasterAPLMutation;
};

export const useUpdateAPL = () => {
  const updateMasterAPLMutation = useMutation({
    mutationKey: UPDATE_APL_KEY,
    mutationFn: updateApl,
  });

  return updateMasterAPLMutation;
};

export const useRollBackMasterAPL = () => {
  const rollbackMasterAPLMutation = useMutation({
    mutationKey: ROLLBACK_MASTER_APL_KEY,
    mutationFn: rollbackMasterAPL,
  });

  return rollbackMasterAPLMutation;
};

export const useCheckMasterAPLExist = () => {
  const { data } = useGetAPLGrid({
    params: { pageIndex: 1, pageSize: 1 },
    key: APL_KEY.getAplGrid(),
  });

  return data?.gridData?.[0]?.aplName === MASTER_APL_NAME;
};

export const useGetAPLDetail = ({ id, enabled }) => {
  return useQuery({
    queryKey: getAPLDetailKey(id),
    queryFn: async () => {
      const response = await getAPLDetail({ aplId: id });
      if (response?.isSuccess) {
        return response?.data;
      } else {
        throw new Error(response?.message);
      }
    },
    enabled: enabled,
    retry: false,
  });
};

export const useCreateEditApl = ({ id, isEdit }) => {
  const [fileList, setFileList] = useState([]);

  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const [formInstance] = Form.useForm();

  const { checkModalVisible, closeAllModal, MODAL_NAMES } = useGlobalModal();

  const createMasterAPLMutation = useCreateAPL();
  const updateAPLMutation = useUpdateAPL();

  const isModalOpen =
    checkModalVisible(MODAL_NAMES.CREATE_MASTER_APL) ||
    checkModalVisible(MODAL_NAMES.EDIT_APL) ||
    checkModalVisible(MODAL_NAMES.CREATE_APL);

  const isSubmitLoading = useIsMutating({
    mutationKey: isEdit ? UPDATE_APL_KEY : CREATE_APL_KEY,
  });

  const reloadAPLPage = () => {
    queryClient.invalidateQueries({
      queryKey: ['apl'],
    });

    dispatch(toggleReloadGridApl());
    setTimeout(() => {
      dispatch(resetReloadGridApl());
    }, 200);
  };

  const getFormData = (data) => {
    let fromData = new FormData();

    if (data) {
      for (const [key, value] of Object.entries(data)) {
        if (key === 'enableScheduleMatching') {
          fromData.append(key, value ?? false);
        } else if (key === 'templateFile') {
          value && fromData.append(key, value?.file);
        } else {
          value && fromData.append(key, value);
        }
      }
    }

    return fromData;
  };

  const handleUpdateAPL = (value) => {
    let newFormatValue = { ...value, aplId: id };

    const params = newFormatValue;

    updateAPLMutation.mutate(params, {
      onSuccess: (result) => {
        if (result?.isSuccess) {
          CustomNotification.success('Update APL successfully');
          dispatch(getAplDetail({ aplId: id }));
          reloadAPLPage();
          closeAllModal();
        } else {
          CustomNotification.error(result?.message || 'Something went wrong!');
        }
      },
    });
  };

  const handleCreateAPL = (value) => {
    let newFormatValue = { ...value };
    if (
      'templateFile' in value &&
      value?.templateFile?.fileList?.length === 0
    ) {
      delete newFormatValue.templateFile;
    }
    const params = getFormData(newFormatValue);
    createMasterAPLMutation.mutate(params, {
      onSuccess: (result) => {
        if (result?.isSuccess) {
          CustomNotification.success('Create APL successfully.');
          reloadAPLPage();
          closeAllModal();
        } else {
          CustomNotification.error(result?.message || 'Something went wrong!');
        }
      },
      onError: () => {
        CustomNotification.error('Something went wrong!');
      },
    });
  };

  const handleSubmitForm = () => {
    formInstance.validateFields().then((value) => {
      if (isEdit) {
        handleUpdateAPL(value);
      } else {
        handleCreateAPL(value);
      }
    });
  };

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      let newFile = file;
      if (file.type !== SPREADSHEET_FILE_TYPE) {
        newFile = {
          name: file?.name,
          uid: file?.uid,
          originFile: file,
          status: 'error',
        };
      }
      setFileList([newFile]);
      return false;
    },
    fileList,
  };

  return {
    isModalOpen,
    isSubmitLoading,
    handleSubmitForm,
    uploadProps,
    closeAllModal,
    isEdit,
    formInstance,
  };
};

export const useCheckProductAdministrator = () => {
  const userInfo = useSelector(selectorUser.makeSelectUserInfo());

  const isSuperAdmin = useCheckIsSuperAdmin();
  const isCompanyProductManager = userInfo?.roles?.includes(
    COMPANY_PRODUCT_MANAGER_ROLE
  );
  const isCompanyAdministrator = userInfo?.roles?.includes(
    COMPANY_ADMINISTRATOR
  );
  return isSuperAdmin || isCompanyProductManager || isCompanyAdministrator;
};

export const useCheckOwnerCompanyId = (companyId) => {
  const userInfo = useSelector(selectorUser.makeSelectUserInfo());
  const checkMasterAPLBelongToMember = () => {
    return userInfo?.companyId === companyId;
  };
  return checkMasterAPLBelongToMember();
};

export const useCheckDisabledMasterAplBtn = (aplDetail) => {
  const isOwnerMember = useCheckOwnerCompanyId(aplDetail?.companyId);
  const isProductAdministrator = useCheckProductAdministrator();

  const checkDisabledBtn = () => {
    return !isProductAdministrator || !isOwnerMember;
  };

  return checkDisabledBtn();
};
